import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { toDateInputValue } from "../../../utils/appointmentLabels";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

function addMonths(dateStr, months) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setMonth(d.getMonth() + months);
  return toDateInputValue(d);
}

function normalizeDate(raw) {
  if (!raw) return "";
  if (typeof raw === "string") return raw.slice(0, 10);
  return new Date(raw).toISOString().slice(0, 10);
}

function nextDay(iso) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return toDateInputValue(d);
}

export default function AdminDoctorVacationPeriod({
  doctorId,
  branchId,
  branches,
  onChanged,
}) {
  const toast = useToast();
  const today = toDateInputValue();
  const [block, setBlock] = useState({
    from: "",
    to: "",
    branch_id: "",
    note: "Отпуск",
  });
  const [closed, setClosed] = useState([]);
  const [listFrom, setListFrom] = useState(today);
  const [listTo, setListTo] = useState(addMonths(today, 6));
  const [loadingList, setLoadingList] = useState(false);

  const loadClosed = useCallback(() => {
    if (!doctorId || !block.branch_id) return;
    setLoadingList(true);
    clinicAPI
      .getDoctorClosedOverrides(doctorId, {
        branch_id: block.branch_id,
        from: listFrom,
        to: listTo,
      })
      .then((r) => setClosed(r.overrides || []))
      .catch(() => setClosed([]))
      .finally(() => setLoadingList(false));
  }, [doctorId, block.branch_id, listFrom, listTo]);

  useEffect(() => {
    if (branchId) {
      setBlock((b) => ({ ...b, branch_id: branchId }));
    } else {
      const def = branches?.find((b) => b.is_default) || branches?.[0];
      if (def) setBlock((b) => ({ ...b, branch_id: def.id }));
    }
  }, [branchId, branches]);

  useEffect(() => {
    loadClosed();
  }, [loadClosed]);

  const blockPeriod = async () => {
    if (!block.from || !block.to || !block.branch_id) {
      toast.error("Укажите период и зал");
      return;
    }
    if (block.from > block.to) {
      toast.error("Дата «с» не может быть позже «по»");
      return;
    }
    try {
      const r = await clinicAPI.blockDoctorPeriod(doctorId, block);
      toast.success(r.message || "Период закрыт");
      const newFrom = block.from < listFrom ? block.from : listFrom;
      const newTo = block.to > listTo ? block.to : listTo;
      setListFrom(newFrom);
      setListTo(newTo);
      const listed = await clinicAPI.getDoctorClosedOverrides(doctorId, {
        branch_id: block.branch_id,
        from: newFrom,
        to: newTo,
      });
      setClosed(listed.overrides || []);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const removeOne = async (id) => {
    try {
      await clinicAPI.deleteScheduleOverride(doctorId, id);
      loadClosed();
      onChanged?.();
      toast.success("День открыт для записи");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const removeRange = async (from, to) => {
    try {
      const r = await clinicAPI.deleteDoctorClosedBulk(doctorId, {
        from,
        to,
        branch_id: block.branch_id,
      });
      toast.success(r.message || "Период снят");
      loadClosed();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const groups = [];
  let cur = null;
  for (const o of closed) {
    const d = normalizeDate(o.date);
    const prev = cur?.dates?.[cur.dates.length - 1];
    if (cur && prev && nextDay(prev) === d && cur.note === (o.note || "Отпуск / блокировка")) {
      cur.dates.push(d);
      cur.ids.push(o.id);
    } else {
      cur = {
        note: o.note || "Отпуск",
        dates: [d],
        ids: [o.id],
      };
      groups.push(cur);
    }
  }

  const rangeHint = listFrom && listTo
    ? `${new Date(`${listFrom}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} — ${new Date(`${listTo}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" })}`
    : "";

  return (
    <div className={s.vacationBlock}>
      <h4 className={s.docSectionTitle}>Отпуск на период</h4>
      <p className={s.docSectionHint}>
        Закрывает каждый день в диапазоне. Для одного дня используйте «Выходной» в
        блоке исключений выше.
      </p>

      <div className={s.vacationFormGrid}>
        <Field label="С" className={s.vacationField}>
          <Input
            type="date"
            value={block.from}
            onChange={(e) => setBlock({ ...block, from: e.target.value })}
          />
        </Field>
        <Field label="По" className={s.vacationField}>
          <Input
            type="date"
            value={block.to}
            onChange={(e) => setBlock({ ...block, to: e.target.value })}
          />
        </Field>
        <Field label="Зал" className={s.vacationField}>
          <Select
            value={block.branch_id}
            onChange={(e) =>
              setBlock({ ...block, branch_id: e.target.value })
            }
          >
            {(branches || []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className={s.vacationFormAction}>
        <Button
          type="button"
          variant="secondary"
          onClick={blockPeriod}
          disabled={!block.branch_id || !block.from || !block.to}
        >
          Закрыть период
        </Button>
      </div>

      <div className={s.vacationListSection}>
        <div className={s.vacationListHead}>
          <div>
            <h5 className={s.vacationListTitle}>Закрытые дни</h5>
            {rangeHint && (
              <p className={s.vacationListRange}>{rangeHint}</p>
            )}
          </div>
          <div className={s.vacationListFilters}>
            <Field label="С" className={s.vacationField}>
              <Input
                type="date"
                value={listFrom}
                onChange={(e) => setListFrom(e.target.value)}
              />
            </Field>
            <Field label="По" className={s.vacationField}>
              <Input
                type="date"
                value={listTo}
                onChange={(e) => setListTo(e.target.value)}
              />
            </Field>
            <div className={s.vacationListFilterBtn}>
              <Button
                type="button"
                variant="secondary"
                onClick={loadClosed}
              >
                Обновить
              </Button>
            </div>
          </div>
        </div>

        {loadingList ? (
          <p className={s.docEmptyHint}>Загрузка…</p>
        ) : groups.length === 0 ? (
          <p className={s.docEmptyHint}>
            В выбранном диапазоне нет закрытых дней. После «Закрыть период» они
            появятся здесь — можно снять блокировку.
          </p>
        ) : (
          <ul className={s.vacationList}>
            {groups.map((g) => {
              const from = g.dates[0];
              const to = g.dates[g.dates.length - 1];
              const label =
                from === to
                  ? new Date(`${from}T12:00:00`).toLocaleDateString("ru-RU", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : `${new Date(`${from}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })} — ${new Date(`${to}T12:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" })}`;
              return (
                <li key={`${from}-${to}-${g.ids[0]}`} className={s.vacationListItem}>
                  <div className={s.vacationListInfo}>
                    <span className={s.vacationListDates}>{label}</span>
                    <span className={s.vacationListMeta}>
                      {g.dates.length} дн. · {g.note}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={s.vacationListRemove}
                    onClick={() =>
                      g.dates.length === 1
                        ? removeOne(g.ids[0])
                        : removeRange(from, to)
                    }
                  >
                    {g.dates.length === 1 ? "Открыть день" : "Снять период"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
